import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Business as BusinessIcon,
  PlayArrow as PlayIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import automationData from './automations.json';
import AutomationCard from './components/AutomationCard';
import { Automation } from './types';

// Type assertion for JSON import
const typedAutomationData: Automation[] = automationData as Automation[];

export default function AutomationsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [automations, setAutomations] = useState<Automation[]>([]);

  useEffect(() => {
    setAutomations(typedAutomationData);
  }, []);

  const handleToggleActive = (id: string, isActive: boolean) => {
    setAutomations((prev) =>
      prev.map((auto) =>
        auto.id === id
          ? {
              ...auto,
              isActive,
              status: isActive ? 'active' : 'paused',
            }
          : auto,
      ),
    );
  };

  const handleEdit = (id: string) => {
    console.log('Edit automation:', id);
  };

  const handleDelete = (id: string) => {
    setAutomations((prev) => prev.filter((auto) => auto.id !== id));
  };

  const getFilteredAutomations = () => {
    switch (activeTab) {
      case 0: // All Automations
        return automations;
      case 1: // My Creations
        return automations.filter((auto) => auto.category === 'my-creations');
      case 2: // System Made
        return automations.filter((auto) => auto.category === 'system-made');
      case 3: // Templates
        return automations.filter((auto) => auto.category === 'templates');
      default:
        return automations;
    }
  };

  const filteredAutomations = getFilteredAutomations();

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight={500}>
          Automations
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
        >
          Create Automation
        </Button>
      </Stack>

      {/* Quick Stats */}
      <Stack direction="row" spacing={2}>
        <Stack
          component={Paper}
          elevation={1}
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ flex: 1, p: 2 }}
        >
          <AutoAwesomeIcon color="primary" sx={{ fontSize: 40 }} />
          <Stack>
            <Typography variant="h4" fontWeight={500}>
              {automations.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Automations
            </Typography>
          </Stack>
        </Stack>
        <Stack
          component={Paper}
          elevation={1}
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ flex: 1, p: 2 }}
        >
          <PlayIcon color="success" sx={{ fontSize: 40 }} />
          <Stack>
            <Typography variant="h4" fontWeight={500}>
              {automations.filter((a) => a.isActive).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active
            </Typography>
          </Stack>
        </Stack>
        <Stack
          component={Paper}
          elevation={1}
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ flex: 1, p: 2 }}
        >
          <TimerIcon color="info" sx={{ fontSize: 40 }} />
          <Stack>
            <Typography variant="h4" fontWeight={500}>
              {automations.filter((a) => a.trigger === 'time').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Time-based
            </Typography>
          </Stack>
        </Stack>
        <Stack
          component={Paper}
          elevation={1}
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ flex: 1, p: 2 }}
        >
          <BusinessIcon color="secondary" sx={{ fontSize: 40 }} />
          <Stack>
            <Typography variant="h4" fontWeight={500}>
              {automations.filter((a) => a.trigger === 'entity').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Entity-based
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Paper elevation={1}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
        >
          <Tab label={`All Automations (${automations.length})`} />
          <Tab
            label={`My Creations (${automations.filter((a) => a.category === 'my-creations').length})`}
          />
          <Tab
            label={`System Made (${automations.filter((a) => a.category === 'system-made').length})`}
          />
          <Tab
            label={`Templates (${automations.filter((a) => a.category === 'templates').length})`}
          />
        </Tabs>
      </Paper>

      {/* Automation List */}
      <Stack spacing={2}>
        {filteredAutomations.map((automation) => (
          <AutomationCard
            key={automation.id}
            automation={automation}
            onToggleActive={handleToggleActive}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}
      </Stack>
    </Stack>
  );
}
